import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type { InventoryCoveragePolicy, InventoryCoverageResponse } from '@/features/decisions/decisions.api.types';
import { useTenant } from '@/providers/TenantProvider';

export interface UseInventoryCoverageParams {
  scenarioRunId: string;
  runId: string;
  sku: string;
  site: string;
  refExperimentId: string;
  experimentIds: string[];
  period: string;
}

// Owns the inventory-coverage projection for one SKU x Site at a decision date:
// the beginning stack + the forward inventory-position series per policy.
export function useInventoryCoverage({
  scenarioRunId,
  runId,
  sku,
  site,
  refExperimentId,
  experimentIds,
  period,
}: UseInventoryCoverageParams) {
  const { tenantAlias } = useTenant();

  const query = useQuery<InventoryCoverageResponse, Error>({
    queryKey: ['inv-coverage', tenantAlias, scenarioRunId, runId, sku, site, refExperimentId, experimentIds, period],
    enabled: Boolean(scenarioRunId && runId && sku && site && refExperimentId && experimentIds.length > 0 && period),
    retry: false,
    staleTime: 30_000,
    queryFn: () =>
      apiFetch<InventoryCoverageResponse>(tenantAlias, ApiEndpoint.SimulationInventoryCoverage, {
        pathParams: { scenarioRunId, runId, sku, site },
        query: {
          ref_experiment_id: refExperimentId,
          experiment_id: experimentIds,
          period,
        },
      }),
  });

  // The API returns policies in an unspecified order. Pin them to a stable order:
  // the reference (Incumbent) first, then comparisons in the configured
  // experimentIds order (Keystone Forecast → Keystone Forecast & TRnQ Optimized).
  const policies = useMemo<InventoryCoveragePolicy[]>(() => {
    const all = query.data?.policies ?? [];
    const order = new Map(experimentIds.map((id, i) => [id, i]));
    return [...all].sort((a, b) => {
      if (a.is_reference !== b.is_reference) return a.is_reference ? -1 : 1;
      return (order.get(a.experiment_id) ?? Infinity) - (order.get(b.experiment_id) ?? Infinity);
    });
  }, [query.data, experimentIds]);

  return {
    policies,
    planningHorizon: query.data?.planning_horizon ?? null,
    resolved: query.data?.resolved,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
